import { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import api from '../utils/api';
import InfoTip from './InfoTip';

const formatCountdown = (eventTime) => {
  const now = new Date();
  const evt = new Date(eventTime);
  const diffMs = evt - now;
  if (diffMs <= 0) return { label: 'الآن', urgent: true };
  const h = Math.floor(diffMs / 3600000);
  const m = Math.floor((diffMs % 3600000) / 60000);
  const s = Math.floor((diffMs % 60000) / 1000);
  if (h > 0) return { label: `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`, urgent: false };
  return { label: `${m}:${String(s).padStart(2,'0')}`, urgent: true };
};

const NewsItem = ({ n }) => {
  const { label, urgent } = formatCountdown(n.time);
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-medium">
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${n.impact === 'High' ? 'bg-red-500 animate-pulse' : 'bg-green-400'}`} />
      <span className="font-bold">{n.title_ar || n.title}</span>
      <span className="opacity-60">({n.country_ar})</span>
      <span className={`font-mono font-bold px-1 py-0.5 rounded text-[9px] ${
        urgent ? 'bg-red-500/30 text-red-300' : 'bg-green-500/20 text-green-300'
      }`}>
        {label}
      </span>
      <span className="opacity-20 mx-1">|</span>
    </span>
  );
};

const EconomicAlert = () => {
  const [news, setNews] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [, setTick] = useState(0);

  const trackRef = useRef(null);
  const posRef = useRef(0);
  const draggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartPosRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await api.get('/economic-news');
        if (Array.isArray(res.data) && res.data.length > 0) setNews(res.data);
      } catch {}
    };
    fetchNews();
    const fetchInterval = setInterval(fetchNews, 600000);
    const tickInterval = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(fetchInterval); clearInterval(tickInterval); };
  }, []);

  // RAF-based auto-scroll using only refs (no React state in loop)
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const animate = () => {
      if (!draggingRef.current && track) {
        posRef.current += 0.5;
        const half = track.scrollWidth / 2;
        if (half > 0 && posRef.current >= half) posRef.current = 0;
        track.style.transform = `translateX(${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [news]);

  const handleStart = useCallback((e) => {
    draggingRef.current = true;
    dragStartXRef.current = e.pageX ?? e.touches?.[0]?.pageX ?? 0;
    dragStartPosRef.current = posRef.current;
  }, []);

  const handleMove = useCallback((e) => {
    if (!draggingRef.current || !trackRef.current) return;
    const x = e.pageX ?? e.touches?.[0]?.pageX ?? 0;
    const diff = x - dragStartXRef.current;
    let newPos = dragStartPosRef.current + diff;
    const half = trackRef.current.scrollWidth / 2;
    if (half > 0) {
      if (newPos < 0) newPos = half + (newPos % half);
      if (newPos >= half) newPos = newPos % half;
    }
    posRef.current = newPos;
    trackRef.current.style.transform = `translateX(${newPos}px)`;
  }, []);

  const handleEnd = useCallback(() => { draggingRef.current = false; }, []);

  const activeNews = news.filter(n => {
    const diff = new Date(n.time) - new Date();
    return diff > -300000;
  });

  if (dismissed || activeNews.length === 0) return null;

  return (
    <div
      className="relative w-full px-2 py-1.5 flex items-center gap-2 text-sm bg-green-500/15 border-b border-green-500/30 text-green-400"
      dir="rtl"
      data-testid="economic-alert-banner"
    >
      <AlertTriangle className="w-4 h-4 shrink-0 text-green-400" />
      <InfoTip
        testId="tip-economic-alert"
        learnSection="alerts-news"
        align="right"
        size={12}
        text="شريط الأخبار الاقتصادية القادمة. النقطة الحمراء = خبر قوي التأثير (قد يحرّك الذهب $10-$50). قاعدة الأمان: لا تفتح صفقة جديدة قبل الخبر بـ 30 دقيقة، وحرّك SL إلى نقطة الدخول لصفقاتك المفتوحة."
      />

      <div
        className="flex-1 overflow-hidden cursor-grab active:cursor-grabbing select-none"
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        data-testid="economic-ticker-container"
      >
        <div
          ref={trackRef}
          className="flex items-center gap-3 whitespace-nowrap w-max"
          data-testid="economic-ticker-track"
        >
          {activeNews.map((n, i) => <NewsItem key={`a-${i}`} n={n} />)}
          {activeNews.map((n, i) => <NewsItem key={`b-${i}`} n={n} />)}
        </div>
      </div>

      <button
        onClick={() => setDismissed(true)}
        className="shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors text-green-400/60 hover:text-green-400"
        data-testid="dismiss-economic-alert"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default EconomicAlert;
